
import {defineComponent} from "vue";
import {ContentPageRestService} from "@/services/rest/cms-content-page/ContentPageRestService";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {PageReferencesContentRestDtoModel} from "@/models/PageReferencesContentRestDtoModel";
import {DpException} from "@/exception/DpException";
import {PageReferenceTypeEnum} from "@/models/enum/PageReferenceTypeEnum";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {OrderFieldEnum} from "@/api/enums/order-field-enum";
import {PageReferenceOrderByRestDtoModel} from "@/models/PageReferenceOrderByRestDtoModel";
import Subtitle from "@/components/UI/Subtitle.vue";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputCheckbox from "@/components/UI/InputCheckbox.vue";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import {MoveActionEnum} from "@/models/enum/MoveActionEnum";
import InputNumber from "@/components/UI/InputNumber.vue";
import { DpEvent } from "@/models/DpEvent";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";

export default defineComponent({
  name: "CbPageReferences",
  components: {InputNumber, InputCheckboxBoolean, InputCheckbox, InputSelect, BaseSpinner, Subtitle},
  extends: CbStructureMixin,
  data: () => ({
    allowedOrderFieldsForExtRef: [
      OrderFieldEnum.SORT_WEIGHT,
      OrderFieldEnum.CREATED
    ] as string[],
    contentBlock: PageReferencesContentRestDtoModel.createWithDefaults(),
    referenceType: PageReferenceTypeEnum.INTERNAL as PageReferenceTypeEnum,
    selectedOrderFilter: PageReferenceOrderByRestDtoModel.createWithDefaults(),
    // todo tmp fix until label functionality implemented
    filterLabel: null as string | null,
    useFilterShowOnHomepage: false as boolean,
    imageFitOptions: [] as CodeDescriptionRestDto[],
    imageHorizontalPositionOptions: [] as CodeDescriptionRestDto[],
    imageVerticalPositionOptions: [] as CodeDescriptionRestDto[],
    orderDirectionOptions: [] as CodeDescriptionRestDto[],
    orderFieldOptionAllValues: [] as CodeDescriptionRestDto[],
    overviewPageLayoutOptions: [] as CodeDescriptionRestDto[],
    visibilityOptions: [] as CodeDescriptionRestDto[],
    pageAbstractLibraries: [] as CodeDescriptionRestDto[],
    pageTypes: [] as CodeDescriptionRestDto[],
  }),
  mounted() {
    this.initialize();
  },
  computed: {
    PageReferenceTypeEnum() {
      return PageReferenceTypeEnum
    },
    MoveActionEnum() {
      return MoveActionEnum;
    },
    pageAbstractLibOptions(): CodeDescriptionRestDto[] {
      return this.pageAbstractLibraries;
    },
    pageTypeOptions(): CodeDescriptionRestDto[] {
      return this.pageTypes;
    },
    orderFieldOptions(): CodeDescriptionRestDto[] {
      if (this.referenceType === PageReferenceTypeEnum.EXTERNAL) {
        return this.orderFieldOptionAllValues
          .filter((order_field: CodeDescriptionRestDto) => this.allowedOrderFieldsForExtRef.includes(order_field.code));
      }
      return this.orderFieldOptionAllValues;
    },
  },
  methods: {
    async find(payload: FindNormalContentBlockPayload): Promise<PageReferencesContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findPageReferences(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createPageReferences(payload.cb as PageReferencesContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().savePageReferences(payload.cb as PageReferencesContentRestDtoModel, payload.type, payload.id);
    },
    async initialize(): Promise<void> {
      [
        this.imageFitOptions,
        this.imageHorizontalPositionOptions,
        this.imageVerticalPositionOptions,
        this.orderDirectionOptions,
        this.orderFieldOptionAllValues,
        this.overviewPageLayoutOptions,
        this.visibilityOptions
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_FIT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_HORIZONTAL_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_VERTICAL_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.ORDER_DIRECTION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.PAGE_REFS_ORDER_FIELD, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OVERVIEW_PAGE_LAYOUT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.PAGE_REFS_VISIBILITY, DpModuleEnum.CMS)
      ]);
    },
    determineEnums(): EnumClassNameCmsEnum[] {
      return [
        // EnumClassNameCmsEnum.IMAGE_HEIGHT,
        EnumClassNameCmsEnum.OBJECT_FIT,
        EnumClassNameCmsEnum.OBJECT_HORIZONTAL_POSITION,
        EnumClassNameCmsEnum.OBJECT_VERTICAL_POSITION,
        EnumClassNameCmsEnum.ORDER_DIRECTION,
        EnumClassNameCmsEnum.OVERVIEW_PAGE_LAYOUT,
        EnumClassNameCmsEnum.PAGE_REFS_ORDER_FIELD,
        EnumClassNameCmsEnum.PAGE_REFS_VISIBILITY
      ];
    },
    async beforeReloadContent(_event: DpEvent): Promise<void> {
      [this.pageAbstractLibraries, this.pageTypes] = await Promise.all([
        ContentPageRestService.getInstance().findPageAbstractLibraries(),
        ContentPageRestService.getInstance().findContentPageTypes(false),
      ]);
    },
    afterReloadContent(): void {
      if (this.contentBlock.page_type_code) {
        this.referenceType = PageReferenceTypeEnum.INTERNAL;
      } else if (this.contentBlock.page_abstr_lib_code) {
        this.referenceType = PageReferenceTypeEnum.EXTERNAL;
      } else {
        this.referenceType = PageReferenceTypeEnum.INTERNAL;
      }

      if (!this.contentBlock.order_by_fields) {
        this.contentBlock.order_by_fields = [];
      }

      if (typeof this.contentBlock.filter_show_on_homepage !== "boolean") {
        this.useFilterShowOnHomepage = false;
        this.contentBlock.filter_show_on_homepage = false;
      } else {
        this.useFilterShowOnHomepage = true;
      }

      // todo tmp fix until label functionality implemented
      if (this.contentBlock.filter_label && this.contentBlock.filter_label.length > 0) {
        this.filterLabel = this.contentBlock.filter_label[0];
      }
    },
    beforeSubmitData(): void {
      switch (this.referenceType) {
        case PageReferenceTypeEnum.INTERNAL:
          this.contentBlock.page_abstr_lib_code = null;
          break;
        case PageReferenceTypeEnum.EXTERNAL:
          this.contentBlock.page_type_code = null;

          this.contentBlock.filter_label = null;
          this.contentBlock.filter_show_on_homepage = null;
          break;
        default:
          throw [new DpException('Unrecognized reference type')];
      }

      if (
        (!this.contentBlock.page_abstr_lib_code && !this.contentBlock.page_type_code) ||
        (this.referenceType === PageReferenceTypeEnum.INTERNAL && !this.contentBlock.page_type_code) ||
        (this.referenceType === PageReferenceTypeEnum.EXTERNAL && !this.contentBlock.page_abstr_lib_code)
      ) {
        throw [new DpException(this.$t('Reference needs to be added.'))];
      }

      if (!this.useFilterShowOnHomepage) {
        this.contentBlock.filter_show_on_homepage = null;
      }

      // todo tmp fix until label functionality implemented
      if (this.filterLabel && this.filterLabel.length > 0) {
        this.contentBlock.filter_label = [this.filterLabel];
      } else {
        this.contentBlock.filter_label = undefined;
      }
    },
    afterSubmitData() {
      if (typeof this.contentBlock.filter_show_on_homepage !== "boolean") {
        this.useFilterShowOnHomepage = false;
        this.contentBlock.filter_show_on_homepage = false;
      }
    },
    getDefaultCbModel(): PageReferencesContentRestDtoModel {
      return PageReferencesContentRestDtoModel.createWithDefaults();
    },
    addOrderFilter(): void {
      this.contentBlock.order_by_fields?.push(JSON.parse(JSON.stringify(this.selectedOrderFilter)));

      this.onChange();
    },
    deleteOrderFilter(index: number): void {
      this.contentBlock.order_by_fields?.splice(index, 1);

      this.onChange();
    },
    moveOrderFilter(index: number, move_direction: MoveActionEnum): void {
      switch (move_direction) {
        case MoveActionEnum.UP:
          if (index !== 0) {
            this.contentBlock.order_by_fields?.splice(index - 1, 0, this.contentBlock.order_by_fields?.splice(index, 1)[0]);

            this.onChange();
          }
          break;
        case MoveActionEnum.DOWN:
          if (index - 1 !== this.contentBlock.order_by_fields?.length) {
            this.contentBlock.order_by_fields?.splice(index + 1, 0, this.contentBlock.order_by_fields?.splice(index, 1)[0]);

            this.onChange();
          }
          break;
      }
    },
    toggleReferenceType(reference_type: PageReferenceTypeEnum): void {
      this.referenceType = reference_type;

      this.onChange();
    },
    pasteCbMetadata(): void {
      this.contentBlock.cloneMetadataFrom(this.getClipboardContentBlock(this.cbType) as PageReferencesContentRestDtoModel);
    },
  }
});
